import {
  Grid,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stepper,
  Step,
  StepLabel,
  Menu,
  MenuItem,
  Tooltip,
} from '@material-ui/core';
import { useState } from 'react';
import { AppraisalPaymentLinkModal } from './appraisalPaymentLinkModal';
import {
  AppraisalDetailsV2Store,
  appraisalSteps,
  formatCurrency,
  Valuation,
  ValuationProperty,
} from 'libs/feature-documents/src/documents/stores/documentForms/appraisalDetailsV2/appraisalDetailsV2Store';
import { Button, IconButton } from '@roc/ui';
import { PrimaryChip, StatusChip, SubTypeChip } from './appraisalDetailsComponents';
import {
  ExpandMore,
  MoreVert,
  GetApp,
  Refresh,
  RateReview,
  Input,
  DescriptionOutlined,
  Grade,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import { DisputeCreatedResult, DISPUTE_TYPE_CLIENT, ReconsiderationOfValueDialog } from './actions/valuationReconsiderationOfValue';
import { SendValuationRevisionDialog } from './actions/valuationSendRevision';
import { ActionContext, AppraisalAction, VALUATION_ACTIONS } from '../utils/appraisalDetailsV2Constants';
import { useStore } from '@roc/client-portal-shared/hooks';
import { useUserStore } from '@roc/feature-app-core';
import { ValuationDisputeDialog } from '../../valuationDispute/valuationDisputeDialog';

export const AppraisalDetailsTab = observer(({
  store, loanId
}: {
  store: AppraisalDetailsV2Store;
  loanId: number;
}) => {
  const { properties } = store;

  return (
    <>
      {properties.map((property) => (
        <AppraisalDetailsProperty
          store={store}
          key={property.propertyId}
          property={property}
          loanId={loanId}
        />
      ))}
    </>
  );
});

const usePropertyStyles = makeStyles(theme => ({
  propertyCard: {
    boxShadow: 'none',
    marginBottom: '16px',
    border: '1px solid rgba(230, 234, 237, 1)',
    borderRadius: '8px !important',
    overflow: 'hidden',
    '& .MuiAccordionSummary-root': {
      backgroundColor: 'rgba(247, 248, 252, 1)',
    },
  },
}));

export const AppraisalDetailsProperty = observer(({
  property,
  store, loanId
}: {
  property: ValuationProperty;
  store: AppraisalDetailsV2Store;
  loanId: number;
}) => {
  const classes = usePropertyStyles();
  const valuations = store.valuations.filter(v => v.propertyId === property.propertyId);
  const defaultExpanded = store.properties.length > 3 ? false : true;

  return (
    <Accordion
      className={classes.propertyCard}
      defaultExpanded={defaultExpanded}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box width="100%">
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
            {property.propertyAddress}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box flex={1} pt={1}>
          {valuations.map(valuation => (
            <AppraisalDetailsOrder
              key={valuation.clientValuationReferenceId}
              store={store}
              valuation={valuation}
              loanId={loanId}
            />
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
});

const useOrderStyles = makeStyles(theme => ({
  orderCard: {
    boxShadow: 'none',
    marginBottom: '16px',
    border: '1px solid rgba(229, 231, 235, 1)',
    borderRadius: '8px !important',
    overflow: 'hidden',
    '& .MuiAccordionSummary-root': {
      backgroundColor: 'rgba(250, 250, 250, 1)',
    },
    '& .MuiAccordionSummary-root.Mui-expanded': {
      borderBottom: '1px solid rgba(229, 231, 235, 1)',
    },
    '& .MuiAccordionSummary-content': {
      margin: '0',
    },
    '& .MuiAccordionDetails-root': {
      margin: '0',
    },
  },
  orderInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginTop: '8px',
  },
  progressStepper: {
    padding: 0,
    '& .MuiStepper-root': {
      backgroundColor: 'transparent',
      padding: 0,
    },
    '& .MuiStepIcon-root': {
      width: '8px',
    },
    '& .MuiStepIcon-text': {
      display: 'none',
    },
    '& .MuiStepLabel-label': {
      fontSize: '12px',
      marginTop: '4px',
      marginLeft: '-16px',
      marginRight: '-16px',
    },
    '& .MuiStepConnector-line': {
      margin: '0 -14px',
      marginTop: '-1px',
      borderTopWidth: '3px',
    },
  },
  detailsGrid: {
    padding: '16px',
    flex: 1,
  },
  detailLabel: {
    fontSize: '12px',
    color: '#666',
    marginBottom: '4px',
  },
  detailValue: {
    fontSize: '14px',
    fontWeight: 500,
  },
}));

const actionIcons: Partial<Record<AppraisalAction, React.ReactNode>> = {
  [AppraisalAction.DOWNLOAD_REPORT]: <GetApp style={{ marginRight: '8px' }} />,
  [AppraisalAction.START_DISPUTE]: <Refresh style={{ marginRight: '8px' }} />,
  [AppraisalAction.VALUATION_REORDER]: <Input style={{ marginRight: '8px' }} />,
  [AppraisalAction.VALUATION_RECERTIFICATION]: <DescriptionOutlined style={{ marginRight: '8px' }} />,
  [AppraisalAction.SET_AS_PRIMARY]: <Grade style={{ marginRight: '8px' }} />,
};

const AppraisalDetailsOrder = observer(
  ({ store, valuation, loanId }: { store: AppraisalDetailsV2Store; valuation: Valuation, loanId: number }) => {
    const classes = useOrderStyles();
    const { globalStore } = useStore();
    const { userStore } = useUserStore();
    const [showROVModal, setShowROVModal] = useState(false);
    const [showSendRevisionModal, setshowSendRevisionModal] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [showDisputeFormDialog, setShowDisputeFormDialog] = useState(false);
    const [disputeCreated, setDisputeCreated] = useState<DisputeCreatedResult | null>(null);

    const property = store.properties.find(p => p.propertyId === valuation.propertyId);
    const propertyAddress = property?.propertyAddress || property?.address || '';

    const ctx: ActionContext = {
      isInternal: globalStore.isInternalPortal,
      isTechUser: userStore.isTechUser,
      isCommitteeUser: userStore.isCommittee,
      valuationStatus: valuation.valuationStatus,
      hasPendingPayment: store.pendingPaymentsCount > 0,
    };
    const visibleActions = VALUATION_ACTIONS.filter(a => a.isVisible(ctx));

    const orderNumberLabel = valuation.orderNumber != null
      ? valuation.orderNumber
      : '--';

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
      setAnchorEl(null);
    };

    const handleActionClick = (action: AppraisalAction) => {
      if (action === AppraisalAction.DOWNLOAD_REPORT) {
        void store.appraisalActionsStore.downloadValuationReport(
          loanId,
          valuation.propertyId,
          valuation.clientValuationReferenceId
        );
      }
      if (action === AppraisalAction.START_DISPUTE) {
        setShowROVModal(true);
      }
      if (action === AppraisalAction.SET_AS_PRIMARY && valuation.draftNumber != null) {
        void store.appraisalActionsStore.setAsSelectedPrimary(
          Number(valuation.draftNumber),
          () => store.fetchAppraisalDetails(loanId)
        );
      }
      handleMenuClose();
    };

    const stepStatusAliases: Record<string, string[]> = {
      Paid: [
        'paid'
      ],
      'Order Placed': [
        'draft',
        'unassigned',
        'unassigned - awaiting quotes',
        'unassigned - expired',
        'accepted by vendor'
      ],
      Assigned: [
        'assigned',
        'assigned to vendor',
        'assigned by vendor',
        'assigned by vendor with conditions',
        'assigned to vendor with conditions',
        'regular assignment',
        'orderassigned',
        'vendor-orderassignedtoappraiser',
        'regular assignment'
      ],
      Scheduled: [
        'scheduled',
        'inspection scheduled',
        'vendor-appointmenttimeset',
        'InspectionScheduled'
      ],
      'Inspection Done': [
        'inspected',
        'inspection completed',
        'inspected but missing docs',
        'vendor-inspected',
      ],
      Completed: [
        'completed',
        'ordercompleted',
        'dispute in progress',
      ],
      Approved: [
        'approved',
      ],
    };

    const normalize = (value?: string) => value?.toLowerCase().trim() ?? '';

    const getStepIndex = (status?: string) =>
      appraisalSteps.findIndex(step =>
        [step, ...(stepStatusAliases[step] ?? [])]
          .map(normalize)
          .includes(normalize(status))
      );

    const currentStepIndex = getStepIndex(valuation.valuationStatus);


    return (
      <Accordion className={classes.orderCard} defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box width="100%" display="flex" alignItems="center">
            <Box width="10%" overflow="hidden" minWidth={0} paddingRight={0.5}>
              <Tooltip title={orderNumberLabel} placement="top">
                <Typography
                  variant="body2"
                  noWrap
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {orderNumberLabel}
                </Typography>
              </Tooltip>
            </Box>
            <Box width="10%" display="flex" justifyContent="center" alignItems="center">
              <StatusChip status={valuation.valuationStatus} />
            </Box>
            <Box width="20%" display="flex" justifyContent="center" alignItems="center">
              {valuation.selectedPrimary && <PrimaryChip />}
            </Box>
            <Box width="60%" className={classes.progressStepper}>
              <Stepper activeStep={currentStepIndex} alternativeLabel>
                {appraisalSteps.map(label => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
            <Box width="20%" display="flex" justifyContent="flex-end" alignItems="center" paddingRight={1}>
              {valuation.subType && <SubTypeChip label={valuation.subType} />}
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box width="100%" display="flex">
            <Grid container spacing={2} className={classes.detailsGrid}>
              <Grid item xs={3}>
                <Typography className={classes.detailLabel}>As-Is Value</Typography>
                <Typography className={classes.detailValue}>
                  {formatCurrency(valuation.currentMarketValue)}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography className={classes.detailLabel}>After Repair Value</Typography>
                <Typography className={classes.detailValue}>
                  {formatCurrency(valuation.afterRepairValue)}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography className={classes.detailLabel}>Valuation Type</Typography>
                <Typography className={classes.detailValue}>
                  {valuation.valuationType ?? '-'}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography className={classes.detailLabel}>Appraiser Name</Typography>
                <Typography className={classes.detailValue}>
                  {valuation.appraiserName ?? '-'}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography className={classes.detailLabel}>Order Date</Typography>
                <Typography className={classes.detailValue}>
                  {valuation.orderDate ?? '-'}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography className={classes.detailLabel}>Inspection Date</Typography>
                <Typography className={classes.detailValue}>
                  {valuation.inspectionDate ?? '-'}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography className={classes.detailLabel}>Completion Date</Typography>
                <Typography className={classes.detailValue}>
                  {valuation.completionDate ?? '-'}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography className={classes.detailLabel}>Due Date</Typography>
                <Typography className={classes.detailValue}>
                  {valuation.dueDate ?? '-'}
                </Typography>
              </Grid>
            </Grid>
            {visibleActions.length > 0 && (
              <IconButton
                size="small"
                onClick={e => handleMenuClick(e)}
                testId="property-menu"
                style={{ alignSelf: 'center' }}
              >
                <MoreVert />
              </IconButton>
            )}
          </Box>
        </AccordionDetails>
        {visibleActions.length > 0 && (
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            {visibleActions.map(action => (
              <MenuItem key={action.key} onClick={() => handleActionClick(action.key)}>
                {actionIcons[action.key]}
                {action.label}
              </MenuItem>
            ))}
          </Menu>
        )}
        <ReconsiderationOfValueDialog
          open={showROVModal}
          onClose={() => setShowROVModal(false)}
          store={store}
          valuation={valuation}
          isInternalUser={ctx.isInternal}
          onDisputeSuccess={(result: DisputeCreatedResult) => {
            setDisputeCreated(result);
            if (result.disputeType === DISPUTE_TYPE_CLIENT) {
              setShowDisputeFormDialog(true);
            }
            // TODO: open ROV form when INTERNAL_VALUATION_ROV_APPRAISAL form is implemented
          }}
        />
        <ValuationDisputeDialog
          open={showDisputeFormDialog && disputeCreated?.disputeType === DISPUTE_TYPE_CLIENT}
          onClose={() => {
            setShowDisputeFormDialog(false);
            setDisputeCreated(null);
          }}
          document={{
            loanId,
            collateralId: valuation?.propertyId,
            collateralAddress: propertyAddress,
            taskId: disputeCreated?.taskId,
            title: disputeCreated?.taskTitle ?? 'Client Valuation Dispute',
            taskName: disputeCreated?.taskTitle ?? 'Client Valuation Dispute',
          } as any}
        />
        <AppraisalPaymentLinkModal
          open={store.showPaymentLinkModal}
          onClose={() => store.setShowPaymentLinkModal(false)}
          store={store}
          loanId={loanId}
        />
        <SendValuationRevisionDialog
          open={showSendRevisionModal}
          onClose={() => setshowSendRevisionModal(false)}
          store={store}
          valuation={valuation}
        />
      </Accordion>
    );
  }
);
